import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  Button,
  EmptyState,
  ErrorNote,
  Loading,
  Modal,
  compactInputClass,
} from '../../components/ui';
import { errorMessage } from '../../lib/api';
import { useAuth } from '../../lib/auth-context-value';
import { itemLabel } from '../../lib/automation-model';
import { formatDateTime } from '../../lib/format';
import { automationExecutionsApi, automationsApi } from '../../lib/resources';
import type { AutomationExecutionStatus, AutomationExecutionStep } from '../../types';

const EXECUTION_STATUS: Record<AutomationExecutionStatus, [string, string]> = {
  running: ['Rodando', 'bg-sky-50 text-sky-700'],
  waiting: ['Aguardando', 'bg-amber-50 text-amber-700'],
  success: ['Concluída', 'bg-emerald-50 text-emerald-700'],
  error: ['Erro', 'bg-red-50 text-red-700'],
  cancelled: ['Cancelada', 'bg-slate-100 text-slate-600'],
};

const OUTCOME: Record<AutomationExecutionStep['outcome'], string> = {
  success: 'text-emerald-600',
  alert: 'text-amber-600',
  error: 'text-red-600',
};

const TRIGGER_LABELS: Record<string, string> = {
  'json-http-request-trigger': 'Webhook',
  'message-received-trigger': 'Mensagem recebida',
  'instagram-comment-received-trigger': 'Comentário no Instagram',
  'business-created-trigger': 'Negócio criado',
  'business-stage-entered-trigger': 'Entrou na etapa',
  'business-won-trigger': 'Negócio ganho',
  'business-lost-trigger': 'Negócio perdido',
};

/** Configurações › Execuções de automação — lista, status, passos e erros (Fase 4 / A3). */
export function AutomationExecutionsSection() {
  const [params, setParams] = useSearchParams();
  const automationId = params.get('automacao') ?? '';
  const [status, setStatus] = useState<AutomationExecutionStatus | ''>('');
  const [problems, setProblems] = useState(false);
  const [openId, setOpenId] = useState<string | null>(null);

  const automations = useQuery({
    queryKey: ['automations'],
    queryFn: () => automationsApi.list(),
  });
  const list = useQuery({
    queryKey: ['automation-executions', automationId, status, problems],
    queryFn: () =>
      automationExecutionsApi.list({
        automationId: automationId || undefined,
        status: status || undefined,
        problems: problems || undefined,
        take: 100,
      }),
    refetchInterval: (current) =>
      current.state.data?.data.some((row) => row.status === 'running') ? 2000 : false,
  });

  return (
    <section className="rounded-xl border border-slate-200 bg-white">
      <header className="flex flex-wrap items-end justify-between gap-3 border-b border-slate-200 px-5 py-4">
        <div>
          <h2 className="text-base font-semibold text-slate-900">Execuções de automação</h2>
          <p className="text-xs text-slate-500">
            Cada disparo, com os passos, alertas e erros. Ficam guardadas por 30 dias.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2 text-sm">
          <select
            className={compactInputClass}
            value={automationId}
            onChange={(event) => {
              const next = new URLSearchParams(params);
              if (event.target.value) next.set('automacao', event.target.value);
              else next.delete('automacao');
              setParams(next, { replace: true });
            }}
          >
            <option value="">Todas as automações</option>
            {automations.data?.data.map((automation) => (
              <option key={automation.id} value={automation.id}>
                {automation.name}
              </option>
            ))}
          </select>
          <select
            className={compactInputClass}
            value={status}
            onChange={(event) =>
              setStatus(event.target.value as AutomationExecutionStatus | '')
            }
          >
            <option value="">Todos os status</option>
            {Object.entries(EXECUTION_STATUS).map(([key, [label]]) => (
              <option key={key} value={key}>
                {label}
              </option>
            ))}
          </select>
          <label className="flex items-center gap-1.5 text-slate-600">
            <input
              type="checkbox"
              checked={problems}
              onChange={(event) => setProblems(event.target.checked)}
            />
            Só com alerta ou erro
          </label>
        </div>
      </header>

      {list.isLoading ? (
        <Loading />
      ) : list.isError ? (
        <div className="p-5">
          <ErrorNote>{errorMessage(list.error)}</ErrorNote>
        </div>
      ) : !list.data?.data.length ? (
        <div className="p-5">
          <EmptyState
            title="Nenhuma execução"
            hint="Ligue uma automação e dispare o gatilho."
          />
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-4 py-2 font-medium">Início</th>
                <th className="px-4 py-2 font-medium">Automação</th>
                <th className="px-4 py-2 font-medium">Gatilho</th>
                <th className="px-4 py-2 font-medium">Status</th>
                <th className="px-4 py-2 font-medium">Passos</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {list.data.data.map((row) => {
                const [label, tone] = EXECUTION_STATUS[row.status];
                return (
                  <tr
                    key={row.id}
                    className="cursor-pointer hover:bg-slate-50"
                    onClick={() => setOpenId(row.id)}
                  >
                    <td className="whitespace-nowrap px-4 py-2.5 text-slate-600">
                      {formatDateTime(row.startedAt)}
                    </td>
                    <td className="px-4 py-2.5 font-medium text-slate-800">
                      {row.automationName ?? '—'}
                      {row.depth > 0 && (
                        <span className="ml-1 text-xs font-normal text-slate-400">
                          nível {row.depth}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-2.5 text-slate-600">
                      {TRIGGER_LABELS[row.trigger] ?? row.trigger}
                    </td>
                    <td className="px-4 py-2.5">
                      <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${tone}`}>
                        {label}
                      </span>
                      {row.status === 'waiting' && row.resumeAt && (
                        <span className="ml-1 text-xs text-slate-400">
                          até {formatDateTime(row.resumeAt)}
                        </span>
                      )}
                    </td>
                    <td className="whitespace-nowrap px-4 py-2.5 text-xs text-slate-500">
                      {row.stepCount}
                      {row.alerts > 0 && (
                        <span className="ml-2 text-amber-600">{row.alerts} alerta(s)</span>
                      )}
                      {row.errors > 0 && (
                        <span className="ml-2 text-red-600">{row.errors} erro(s)</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {openId && <ExecutionModal id={openId} onClose={() => setOpenId(null)} />}
    </section>
  );
}

export function ExecutionModal({ id, onClose }: { id: string; onClose: () => void }) {
  const { can } = useAuth();
  const queryClient = useQueryClient();
  const detail = useQuery({
    queryKey: ['automation-execution', id],
    queryFn: () => automationExecutionsApi.get(id),
  });
  const cancel = useMutation({
    mutationFn: () => automationExecutionsApi.cancel(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['automation-execution', id] });
      void queryClient.invalidateQueries({ queryKey: ['automation-executions'] });
    },
  });
  const data = detail.data;

  return (
    <Modal title="Execução" onClose={onClose}>
      {detail.isLoading || !data ? (
        <Loading />
      ) : (
        <div className="space-y-4 text-sm">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="text-slate-600">
              <span
                className={`rounded-full px-2 py-0.5 text-xs font-medium ${EXECUTION_STATUS[data.status][1]}`}
              >
                {EXECUTION_STATUS[data.status][0]}
              </span>{' '}
              v{data.version} · {formatDateTime(data.startedAt)}
              {data.finishedAt && ` → ${formatDateTime(data.finishedAt)}`}
            </div>
            <div className="flex gap-2">
              {data.leadId && (
                <Link
                  to={`/leads/${data.leadId}`}
                  className="text-sm text-slate-600 hover:underline"
                >
                  Abrir lead
                </Link>
              )}
              {data.businessId && (
                <Link
                  to={`/negocios/${data.businessId}`}
                  className="text-sm text-slate-600 hover:underline"
                >
                  Abrir negócio
                </Link>
              )}
            </div>
          </div>
          {data.error && <ErrorNote>{data.error}</ErrorNote>}
          <ol className="max-h-80 space-y-1 overflow-y-auto rounded-lg border border-slate-100 bg-slate-50 p-3 text-xs">
            {data.steps.map((step, index) => (
              <li key={`${step.at}-${index}`} className="flex gap-2">
                <span className={`shrink-0 font-semibold ${OUTCOME[step.outcome]}`}>
                  {step.outcome === 'success' ? '✓' : step.outcome === 'alert' ? '!' : '✕'}
                </span>
                <span className="text-slate-700">
                  <strong>{step.item ? itemLabel(step.item) : step.blockType}</strong>
                  {step.message && <span className="text-slate-500"> — {step.message}</span>}
                </span>
              </li>
            ))}
          </ol>
          <details className="text-xs text-slate-500">
            <summary className="cursor-pointer">Dados da execução (fontes e lead)</summary>
            <pre className="mt-2 max-h-60 overflow-auto rounded bg-slate-900 p-3 text-slate-100">
              {JSON.stringify(data.context, null, 2)}
            </pre>
          </details>
          {cancel.isError && <ErrorNote>{errorMessage(cancel.error)}</ErrorNote>}
          <div className="flex justify-end gap-2">
            {can('flow_admin') && (data.status === 'waiting' || data.status === 'running') && (
              <Button
                variant="danger"
                disabled={cancel.isPending}
                onClick={() => cancel.mutate()}
              >
                Cancelar execução
              </Button>
            )}
            <Button onClick={onClose}>Fechar</Button>
          </div>
        </div>
      )}
    </Modal>
  );
}
